import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { addressSearch, getAddressAutocompleteApiKey, getAddressData, isLoqateError, retrieveAddress, } from '@/helpers/AddressHelpers';
import AutocompleteInput from '@/components/forms/AutocompleteInput.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import { logInTrackJS } from '@/errors/helpers';
@Component({
    name: 'AddressInput',
    components: { AutocompleteInput, FormInput2 },
})
export default class default_1 extends Vue {
    @Prop()
    value;
    @Prop()
    country;
    @Prop()
    inputMode;
    @Prop()
    field;
    @Prop()
    required;
    t;
    errorInternal = null;
    addresses = null;
    selectedAddress = null;
    minSearchLength = 4;
    timeout;
    searchInput = '';
    focused = false;
    created() {
        if (this.value?.address) {
            this.searchInput = this.value.address;
        }
    }
    get autocompleteEnabled() {
        return Boolean(this.apiKey && this.country && this.inputMode === 'autocomplete');
    }
    get input() {
        return this.value?.address ?? '';
    }
    set input(value) {
        this.$emit('input', { address: value });
    }
    get intermediateInput() {
        return this.searchInput;
    }
    set intermediateInput(value) {
        this.searchInput = value;
        // Handle autofill and manual entry.
        if (!this.focused || !this.autocompleteEnabled) {
            this.selectedAddress = { address: value };
        }
    }
    // Triggered on both new address suggestions and focusing the input.
    @Watch('addresses')
    scrollTo() {
        // TODO Is there a better way of doing this?
        if (this.autocompleteEnabled) {
            this.$nextTick(() => {
                this.$el.scrollIntoView();
            });
        }
    }
    get apiKey() {
        return getAddressAutocompleteApiKey();
    }
    @Watch('searchInput')
    onSearchInputChange(value) {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        if (!this.shouldSearch(value)) {
            this.addresses = null;
            return;
        }
        this.errorInternal = null;
        this.selectedAddress = null;
        if (this.country) {
            const country = this.country;
            this.timeout = setTimeout(() => this.lookupAddresses(country, value), 300);
        }
        else {
            this.addresses = null;
        }
    }
    shouldSearch(input) {
        return (this.autocompleteEnabled &&
            input.length >= this.minSearchLength &&
            (!this.selectedAddress || input !== this.selectedAddress?.address) &&
            this.focused);
    }
    lookupAddresses(country, input) {
        const params = typeof input === 'string' ? { Text: input } : { Container: input.Id };
        addressSearch(country, params)
            .then((items) => {
            this.addresses = items;
        })
            .catch(this.handleError);
    }
    retrieveAddress(address) {
        retrieveAddress(address.Id)
            .then((items) => {
            this.selectedAddress = getAddressData(items[0]);
            this.validate();
        })
            .catch(this.handleError);
    }
    @Watch('selectedAddress')
    onSelectedAddress() {
        this.$emit('input', this.selectedAddress ?? {});
        if (this.selectedAddress) {
            this.searchInput = this.selectedAddress.address;
        }
    }
    handleError(error) {
        // Revert to manual address entry on error.
        this.switchToManualMode();
        logInTrackJS(isLoqateError(error) ? error.Description : error.toString(), error);
    }
    switchToManualMode() {
        this.input = this.searchInput;
        this.$emit('manualModeClick');
    }
    @Watch('inputMode')
    onInputModeChange() {
        if (this.value) {
            this.errorInternal = this.error;
        }
        this.onSearchInputChange(this.input);
    }
    addressSelected(address) {
        if (address?.Type === 'Container') {
            this.lookupAddresses(this.country, address);
        }
        else if (address?.Type === 'Address') {
            this.retrieveAddress(address);
        }
        else {
            this.selectedAddress = null;
        }
    }
    @Watch('disabled')
    validate() {
        const input = this.inputEl;
        this.errorInternal = this.error;
        this.$emit('validation', this.error, input);
    }
    get error() {
        if (this.autocompleteEnabled && !this.selectedAddress) {
            return this.t.errorMessage;
        }
        else {
            return null;
        }
    }
    get inputEl() {
        return this.$el.querySelector('input, select');
    }
    get eventListeners() {
        return {
            ...this.$listeners,
            // Maintain compatibility with v-model.
            // @see https://vuejs.org/v2/guide/components-custom-events.html#Binding-Native-Events-to-Components
            input: () => null,
        };
    }
    get placeholder() {
        if (this.autocompleteEnabled) {
            return this.t.placeholder;
        }
        else if (this.field.showPlaceholderDropdownOption) {
            return this.field.placeholderLanguage;
        }
        else {
            return undefined;
        }
    }
    setFocused(value) {
        this.focused = value;
    }
}
